import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { title: "", due_date: "", priority: "normal", booking_id: "" };

export default function Tasks() {
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/tasks", { params: tab === "all" ? {} : { done: tab === "done" } })
    .then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...form, booking_id: form.booking_id || null });
      toast.success("Task created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { done: !t.done });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const overdue = (t) => !t.done && t.due_date < new Date().toISOString().split("T")[0];

  return (
    <div data-testid="tasks-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tasks &amp; Alerts</h1>
          <p className="mt-1 text-sm text-muted-foreground">Operational reminders linked to events and the diary.</p>
        </div>
        <Button data-testid="new-task-button" onClick={() => setOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> New Task
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border">
        {["open", "done", "all"].map((t) => (
          <button key={t} data-testid={`task-tab-${t}`} onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${tab === t ? "border-b-2 border-ink text-ink" : "text-muted-foreground hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="border border-border bg-white">
        {items.map((t) => (
          <label key={t.id} data-testid={`task-row-${t.id}`} className={`flex cursor-pointer items-center gap-4 border-b border-border px-5 py-3.5 transition-colors duration-150 last:border-0 hover:bg-slate-50 ${t.done ? "opacity-50" : ""}`}>
            <Checkbox checked={t.done} onCheckedChange={() => toggle(t)} data-testid={`task-checkbox-${t.id}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${t.done ? "line-through" : ""}`}>{t.title}</p>
              {t.booking_ref && <p className="font-mono text-xs text-muted-foreground">{t.booking_ref}</p>}
            </div>
            {t.priority === "high" && !t.done && <span className="border border-red-500 bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-red-700">High</span>}
            <span className={`text-xs tabular-nums ${overdue(t) ? "font-bold text-red-600" : "text-muted-foreground"}`} data-testid={`task-due-${t.id}`}>
              {overdue(t) ? "Overdue · " : "Due "}{t.due_date}
            </span>
          </label>
        ))}
        {items.length === 0 && <p className="px-5 py-10 text-center text-sm text-muted-foreground">No tasks here.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm bg-white" data-testid="task-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Task</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="task-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Due date</Label><Input data-testid="task-due-input" type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Linked booking (optional)</Label>
              <Select value={form.booking_id} onValueChange={(v) => setForm({ ...form, booking_id: v })}>
                <SelectTrigger data-testid="task-booking-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_ref} — {b.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="task-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Create Task</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
